import { useEffect, useState, useRef } from 'react';
import { useOutletContext, useNavigate, Link } from 'react-router-dom';
import { getCalls, logActivity, getCallStatistics, getCallAudioUrl, createReviewRequest, getUserReviewRequests } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  Phone, 
  Clock,
  Star,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Filter,
  Calendar,
  X,
  FileText,
  MessageSquare,
  Play,
  Pause,
  CheckCircle2,
  AlertTriangle,
  Info,
  BarChart3,
  TrendingUp,
  Headphones,
  Search,
  Send,
  Loader2
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

// Generate month options for filter
const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleString('default', { month: 'long', year: 'numeric' })
    });
  }
  return months;
};

const MONTH_OPTIONS = getMonthOptions();

const CLASSIFICATIONS = [
  { value: 'all', label: 'All Classifications' },
  { value: 'best', label: 'Best' },
  { value: 'good', label: 'Good' },
  { value: 'improvement', label: 'Improvement' },
  { value: 'high_alert', label: 'High Alert' },
];

const CALL_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'introduction', label: 'Introduction' },
  { value: 'screening', label: 'Screening' },
  { value: 'interview', label: 'Interview' },
  { value: 'followup', label: 'Follow-up' },
  { value: 'client', label: 'Client' },
];

export default function UserCalls() {
  const { user, userUrl } = useOutletContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('calls');
  const [calls, setCalls] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Statistics state
  const [statistics, setStatistics] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsSortField, setStatsSortField] = useState('date');
  const [statsSortOrder, setStatsSortOrder] = useState('desc');
  
  // Filters
  const [filters, setFilters] = useState({
    classification: 'all',
    call_type: 'all',
    date_from: '',
    date_to: '',
    month: 'all',
  });
  
  // Audio player state
  const [playingCallId, setPlayingCallId] = useState(null);
  const audioRef = useRef(null);
  
  // Quick view dialogs
  const [showTranscript, setShowTranscript] = useState(null);
  const [showFeedback, setShowFeedback] = useState(null);
  const [showScore, setShowScore] = useState(null);
  
  // Review request state
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewCall, setReviewCall] = useState(null);
  const [reviewReason, setReviewReason] = useState('');
  const [reviewExpected, setReviewExpected] = useState('');
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  // Fetch statistics for this user
  const fetchStatistics = async () => {
    if (!user?.id) return;
    setStatsLoading(true);
    try {
      const { data } = await getCallStatistics({ user_id: user.id });
      setStatistics(data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, [user?.id]);

  useEffect(() => {
    const fetchCalls = async () => {
      if (!user?.id) return;
      setIsLoading(true);
      try {
        const params = { user_id: user.id };
        if (filters.classification !== 'all') params.classification = filters.classification;
        if (filters.call_type !== 'all') params.call_type = filters.call_type;
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;
        
        if (filters.month !== 'all') {
          const [year, month] = filters.month.split('-');
          const startDate = new Date(year, parseInt(month) - 1, 1);
          const endDate = new Date(year, parseInt(month), 0);
          params.date_from = startDate.toISOString().split('T')[0];
          params.date_to = endDate.toISOString().split('T')[0];
        }
        
        const { data } = await getCalls(params);
        setCalls(data.calls);
        setFilteredCalls(data.calls);
      } catch (error) {
        console.error('Failed to fetch calls:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCalls();
  }, [user?.id, filters]);
  
  // Filter calls based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCalls(calls);
      return;
    }
    
    const query = searchQuery.toLowerCase().trim();
    const filtered = calls.filter(call => {
      // Search in filename
      if (call.filename?.toLowerCase().includes(query)) return true;
      // Search in phone number
      if (call.caller_phone?.includes(query) || call.callee_phone?.includes(query)) return true;
      // Search in call type
      if (call.call_type?.toLowerCase().includes(query)) return true;
      // Search in classification
      if (call.classification?.toLowerCase().includes(query)) return true;
      // Search in transcript (if available)
      if (call.transcript?.toLowerCase().includes(query)) return true;
      // Search in feedback
      if (call.ai_feedback?.toLowerCase().includes(query)) return true;
      // Search by date
      const dateStr = call.created_at ? new Date(call.created_at).toLocaleDateString() : '';
      if (dateStr.includes(query)) return true;
      // Search by score
      if (call.average_score?.toString().includes(query)) return true;
      
      return false;
    });
    
    setFilteredCalls(filtered);
  }, [searchQuery, calls]);

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const clearDateFilters = () => {
    setFilters({...filters, date_from: '', date_to: '', month: 'all'});
  };

  // Quick action handlers
  const handlePlayAudio = async (call, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (playingCallId === call.id) {
      audioRef.current?.pause();
      setPlayingCallId(null);
    } else {
      try {
        if (audioRef.current) {
          // Get authenticated audio URL
          const result = await getCallAudioUrl(call.id);
          if (result.error) {
            toast.error(result.error);
            return;
          }
          audioRef.current.src = result.url;
          audioRef.current.play().catch(() => toast.error('Failed to play audio'));
        }
        setPlayingCallId(call.id);
      } catch (error) {
        console.error('Failed to load audio:', error);
        toast.error(error.response?.status === 404 ? 'No audio file available for this call' : 'Failed to load audio');
      }
    }
  };

  const handleViewTranscript = (call, e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowTranscript(call);
  };

  const handleViewFeedback = (call, e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowFeedback(call);
  };

  const handleViewScore = (call, e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowScore(call);
  };

  const handleCardClick = (call) => {
    navigate(`/user/${userUrl}/call/${call.id}`);
  };
  
  // Generate call detail URL for links
  const getCallDetailUrl = (callId) => `/user/${userUrl}/call/${callId}`;

  // Handle review request
  const handleRequestReview = (call, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setReviewCall(call);
    setReviewReason('');
    setReviewExpected('');
    setShowReviewDialog(true);
  };

  const submitReviewRequest = async () => {
    if (!reviewReason.trim()) {
      toast.error('Please explain why you want this call reviewed');
      return;
    }
    
    setIsSubmittingReview(true);
    try {
      await createReviewRequest(
        {
          call_id: reviewCall.id,
          reason: reviewReason,
          expected_outcome: reviewExpected
        },
        user.id,
        user.name
      );
      
      toast.success('Review request submitted! The QA team will review your call.');
      setShowReviewDialog(false);
      setReviewCall(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes('already pending')) {
        toast.error('A review request for this call is already pending');
      } else {
        toast.error(detail || 'Failed to submit review request');
      }
    } finally {
      setIsSubmittingReview(false);
    }
  };

  // Statistics sorting
  const sortedMonthlyBreakdown = statistics?.monthly_breakdown?.slice().sort((a, b) => {
    if (statsSortField === 'date') return statsSortOrder === 'desc' ? b.month.localeCompare(a.month) : a.month.localeCompare(b.month);
    if (statsSortField === 'calls') return statsSortOrder === 'desc' ? b.calls - a.calls : a.calls - b.calls;
    if (statsSortField === 'hours') return statsSortOrder === 'desc' ? b.duration_hours - a.duration_hours : a.duration_hours - b.duration_hours;
    return 0;
  }) || [];

  const sortedDailyBreakdown = statistics?.daily_breakdown?.slice().sort((a, b) => {
    if (statsSortField === 'date') return statsSortOrder === 'desc' ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date);
    if (statsSortField === 'calls') return statsSortOrder === 'desc' ? b.calls - a.calls : a.calls - b.calls;
    if (statsSortField === 'hours') return statsSortOrder === 'desc' ? b.duration_hours - a.duration_hours : a.duration_hours - b.duration_hours;
    return 0;
  }) || [];

  const toggleSort = (field) => {
    if (statsSortField === field) {
      setStatsSortOrder(statsSortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setStatsSortField(field);
      setStatsSortOrder('desc');
    }
  };

  // Handle month card click - filter calls
  const handleMonthCardClick = (month) => {
    setFilters({ ...filters, month: month.month });
    setActiveTab('calls');
  };

  if (isLoading && !calls.length) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-16" />
        {[...Array(5)].map((_, i) => (<Skeleton key={i} className="h-24" />))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-calls">
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="calls" className="gap-2">
            <Phone className="h-4 w-4" />
            My Calls
          </TabsTrigger>
          <TabsTrigger value="statistics" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            My Statistics
          </TabsTrigger>
        </TabsList>

        {/* My Calls Tab */}
        <TabsContent value="calls" className="space-y-6 mt-6">
          {/* Search Bar */}
          <Card className="card-elevated">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search calls by filename, phone number, call type, score, date, transcript..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-9"
                  data-testid="calls-search-input"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setSearchQuery('')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {searchQuery && (
                <p className="text-xs text-muted-foreground mt-2">
                  Found {filteredCalls.length} of {calls.length} calls matching &quot;{searchQuery}&quot;
                </p>
              )}
            </CardContent>
          </Card>
          
          {/* Filters */}
          <Card className="card-elevated">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Filter className="h-4 w-4" />
                  <span className="text-sm font-medium">Filters:</span>
                </div>
                
                <Select value={filters.classification} onValueChange={(v) => setFilters({...filters, classification: v})}>
                  <SelectTrigger className="w-[180px]"><SelectValue placeholder="Classification" /></SelectTrigger>
                  <SelectContent>
                    {CLASSIFICATIONS.map(cls => (<SelectItem key={cls.value} value={cls.value}>{cls.label}</SelectItem>))}
                  </SelectContent>
                </Select>
                
                <Select value={filters.call_type} onValueChange={(v) => setFilters({...filters, call_type: v})}>
                  <SelectTrigger className="w-[150px]"><SelectValue placeholder="Call Type" /></SelectTrigger>
                  <SelectContent>
                    {CALL_TYPES.map(type => (<SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>))}
                  </SelectContent>
                </Select>
                
                <Badge variant="secondary" className="ml-auto">{filteredCalls.length} calls</Badge>
              </div>
              
              {/* Date Filters Row */}
              <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span className="text-sm font-medium">Date:</span>
                </div>
                
                <Select value={filters.month} onValueChange={(v) => setFilters({...filters, month: v, date_from: '', date_to: ''})}>
                  <SelectTrigger className="w-[180px]"><SelectValue placeholder="Select Month" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    {MONTH_OPTIONS.map(month => (<SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>))}
                  </SelectContent>
                </Select>
                
                <span className="text-sm text-muted-foreground">or</span>
                
                <div className="flex items-center gap-2">
                  <Input type="date" value={filters.date_from} onChange={(e) => setFilters({...filters, date_from: e.target.value, month: 'all'})} className="w-[150px]" />
                  <span className="text-muted-foreground">to</span>
                  <Input type="date" value={filters.date_to} onChange={(e) => setFilters({...filters, date_to: e.target.value, month: 'all'})} className="w-[150px]" />
                </div>
                
                {(filters.date_from || filters.date_to || filters.month !== 'all') && (
                  <Button variant="ghost" size="sm" onClick={clearDateFilters} className="text-muted-foreground">
                    <X className="h-4 w-4 mr-1" /> Clear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Calls Table */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="font-heading flex items-center justify-between">
                <span>My Calls</span>
                <Badge variant="secondary">{filteredCalls.length} total</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredCalls.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[120px]">Date</TableHead>
                        <TableHead className="min-w-[80px]">Time</TableHead>
                        <TableHead className="min-w-[120px]">Call Type</TableHead>
                        <TableHead className="min-w-[130px]">Phone</TableHead>
                        <TableHead className="min-w-[90px]">Duration</TableHead>
                        <TableHead className="min-w-[70px]">Score</TableHead>
                        <TableHead className="min-w-[120px]">Classification</TableHead>
                        <TableHead className="min-w-[100px]">Alert</TableHead>
                        <TableHead className="min-w-[180px]">Quick Actions</TableHead>
                        <TableHead className="w-[40px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCalls.map((call) => (
                        <TableRow 
                          key={call.id} 
                          className={`hover:bg-muted/50 cursor-pointer ${call.alert_level === 'critical' ? 'tracing-beam' : ''}`}
                          onClick={() => navigate(getCallDetailUrl(call.id))}
                          data-testid={`call-row-${call.id}`}
                        >
                          <TableCell className="whitespace-nowrap">
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                              <span className="text-sm">{formatDate(call.call_date)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <span className="text-sm text-muted-foreground">{formatTime(call.call_date)}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize text-xs">{call.call_type?.replace('_', ' ')}</Badge>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <span className="text-xs text-muted-foreground font-mono">{call.phone_number || '-'}</span>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                              <span className="text-sm">{formatDuration(call.duration)}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Star className="h-4 w-4 text-amber-500 flex-shrink-0" />
                              <span className="font-semibold">{call.average_score ?? '-'}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`classification-${call.classification} capitalize text-xs`}>
                              {call.classification?.replace('_', ' ') || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`status-${call.alert_level} capitalize text-xs`}>
                              {call.alert_level === 'critical' && <AlertTriangle className="h-3 w-3 mr-1" />}
                              {call.alert_level || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <TooltipProvider>
                              <div className="flex items-center gap-1">
                                <Tooltip><TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => handlePlayAudio(call, e)}>
                                    {playingCallId === call.id ? <Pause className="h-3.5 w-3.5 text-blue-600" /> : <Play className="h-3.5 w-3.5 text-muted-foreground" />}
                                  </Button>
                                </TooltipTrigger><TooltipContent><p>{playingCallId === call.id ? 'Pause' : 'Play'}</p></TooltipContent></Tooltip>
                                <Tooltip><TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => handleViewScore(call, e)}>
                                    <Star className="h-3.5 w-3.5 text-amber-500" />
                                  </Button>
                                </TooltipTrigger><TooltipContent><p>Score</p></TooltipContent></Tooltip>
                                <Tooltip><TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => handleViewTranscript(call, e)}>
                                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                                  </Button>
                                </TooltipTrigger><TooltipContent><p>Transcript</p></TooltipContent></Tooltip>
                                <Tooltip><TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => handleViewFeedback(call, e)}>
                                    <MessageSquare className="h-3.5 w-3.5 text-emerald-600" />
                                  </Button>
                                </TooltipTrigger><TooltipContent><p>Feedback</p></TooltipContent></Tooltip>
                                <Tooltip><TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => handleRequestReview(call, e)}>
                                    <Send className="h-3.5 w-3.5 text-purple-600" />
                                  </Button>
                                </TooltipTrigger><TooltipContent><p>Request Review</p></TooltipContent></Tooltip>
                              </div>
                            </TooltipProvider>
                          </TableCell>
                          <TableCell>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Phone className="h-12 w-12 text-muted-foreground/70 mx-auto mb-4" />
                  <p className="text-muted-foreground font-medium">No calls found</p>
                  <p className="text-sm text-muted-foreground mt-1">Your analyzed calls will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* My Statistics Tab */}
        <TabsContent value="statistics" className="space-y-6 mt-6">
          {/* Statistics Filters */}
          <Card className="card-elevated">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Filter className="h-4 w-4" />
                  <span className="text-sm font-medium">Filter by Month:</span>
                </div>
                
                <Select value={filters.month} onValueChange={(v) => setFilters({...filters, month: v})}>
                  <SelectTrigger className="w-[200px]">
                    <Calendar className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Select Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    {MONTH_OPTIONS.map(month => (<SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>))}
                  </SelectContent>
                </Select>

                {filters.month !== 'all' && (
                  <Button variant="ghost" size="sm" onClick={() => setFilters({...filters, month: 'all'})} className="text-muted-foreground">
                    <X className="h-4 w-4 mr-1" /> Clear Filter
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Statistics Content */}
          {statsLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (<Skeleton key={i} className="h-32" />))}
            </div>
          ) : statistics ? (
            <>
              {/* Summary Cards - Clickable */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="card-elevated hover:shadow-lg transition-shadow cursor-pointer border-blue-200 hover:border-blue-400" onClick={() => setActiveTab('calls')}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Phone className="h-5 w-5 text-blue-600" />
                      </div>
                      <span className="text-sm text-muted-foreground font-medium">Total Calls</span>
                    </div>
                    <p className="text-4xl font-bold text-foreground">{statistics.total_calls}</p>
                    <p className="text-xs text-blue-500 mt-1">Click to view all calls →</p>
                  </CardContent>
                </Card>
                
                <Card className="card-elevated hover:shadow-lg transition-shadow border-indigo-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <Headphones className="h-5 w-5 text-indigo-600" />
                      </div>
                      <span className="text-sm text-muted-foreground font-medium">Total Hours</span>
                    </div>
                    <p className="text-4xl font-bold text-foreground">{statistics.total_hours.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{statistics.total_minutes.toFixed(0)} minutes transcribed</p>
                  </CardContent>
                </Card>
                
                <Card className="card-elevated hover:shadow-lg transition-shadow border-purple-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <TrendingUp className="h-5 w-5 text-purple-600" />
                      </div>
                      <span className="text-sm text-muted-foreground font-medium">Avg Duration</span>
                    </div>
                    <p className="text-4xl font-bold text-foreground">{statistics.average_duration_minutes.toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground mt-1">minutes per call</p>
                  </CardContent>
                </Card>
                
                <Card className="card-elevated hover:shadow-lg transition-shadow border-emerald-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <Calendar className="h-5 w-5 text-emerald-600" />
                      </div>
                      <span className="text-sm text-muted-foreground font-medium">Active Months</span>
                    </div>
                    <p className="text-4xl font-bold text-foreground">{statistics.monthly_breakdown?.length || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">months with activity</p>
                  </CardContent>
                </Card>
              </div>

              {/* Sorting Controls */}
              <div className="flex items-center gap-4 bg-muted/50 rounded-lg p-3">
                <span className="text-sm font-medium text-muted-foreground">Sort by:</span>
                <Button variant={statsSortField === 'date' ? 'secondary' : 'ghost'} size="sm" onClick={() => toggleSort('date')} className="gap-1">
                  Date {statsSortField === 'date' && (statsSortOrder === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                </Button>
                <Button variant={statsSortField === 'calls' ? 'secondary' : 'ghost'} size="sm" onClick={() => toggleSort('calls')} className="gap-1">
                  Calls {statsSortField === 'calls' && (statsSortOrder === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                </Button>
                <Button variant={statsSortField === 'hours' ? 'secondary' : 'ghost'} size="sm" onClick={() => toggleSort('hours')} className="gap-1">
                  Hours {statsSortField === 'hours' && (statsSortOrder === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                </Button>
              </div>

              {/* Monthly & Daily Tables */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Monthly Breakdown Table */}
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-blue-500" />
                      Monthly Breakdown
                    </CardTitle>
                    <CardDescription>Click on a month to filter calls</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Month</TableHead>
                          <TableHead className="text-right">Calls</TableHead>
                          <TableHead className="text-right">Hours</TableHead>
                          <TableHead className="text-right">Avg (min)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedMonthlyBreakdown.length > 0 ? sortedMonthlyBreakdown.map((month) => (
                          <TableRow key={month.month} className="cursor-pointer hover:bg-blue-50 transition-colors" onClick={() => handleMonthCardClick(month)}>
                            <TableCell className="font-medium">{month.label}</TableCell>
                            <TableCell className="text-right"><Badge variant="secondary">{month.calls}</Badge></TableCell>
                            <TableCell className="text-right text-muted-foreground">{month.duration_hours.toFixed(2)}h</TableCell>
                            <TableCell className="text-right text-muted-foreground">{month.calls > 0 ? ((month.duration_seconds / month.calls) / 60).toFixed(1) : '0'}</TableCell>
                          </TableRow>
                        )) : (
                          <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No data available</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Daily Breakdown Table */}
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="h-5 w-5 text-indigo-500" />
                      Daily Activity
                    </CardTitle>
                    <CardDescription>Your recent call activity by day</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[400px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Day</TableHead>
                            <TableHead className="text-right">Calls</TableHead>
                            <TableHead className="text-right">Hours</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sortedDailyBreakdown.length > 0 ? sortedDailyBreakdown.map((day) => (
                            <TableRow key={day.date} className="hover:bg-indigo-50 transition-colors">
                              <TableCell className="font-medium">{day.label}</TableCell>
                              <TableCell className="text-muted-foreground">{day.day_of_week}</TableCell>
                              <TableCell className="text-right"><Badge variant="outline">{day.calls}</Badge></TableCell>
                              <TableCell className="text-right text-muted-foreground">{day.duration_hours.toFixed(2)}h</TableCell>
                            </TableRow>
                          )) : (
                            <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No data available</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <BarChart3 className="h-12 w-12 text-muted-foreground/70 mx-auto mb-4" />
              <p className="text-muted-foreground">No statistics available</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Hidden Audio Element */}
      <audio ref={audioRef} onEnded={() => setPlayingCallId(null)} className="hidden" />

      {/* Quick View Score Dialog */}
      <Dialog open={!!showScore} onOpenChange={() => setShowScore(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Star className="h-5 w-5 text-amber-500" /> Score Details</DialogTitle>
            <DialogDescription>{showScore && `${showScore.call_type} call - ${formatDate(showScore.call_date)}`}</DialogDescription>
          </DialogHeader>
          {showScore && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-4xl font-bold text-foreground">{showScore.average_score}</p>
                <p className="text-sm text-muted-foreground">Overall Score</p>
                <Badge className={`classification-${showScore.classification} capitalize mt-2`}>{showScore.classification?.replace('_', ' ')}</Badge>
              </div>
              {showScore.score_breakdown && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Score Breakdown</p>
                  {Object.entries(showScore.score_breakdown).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="font-semibold">{value}/10</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick View Transcript Dialog */}
      <Dialog open={!!showTranscript} onOpenChange={() => setShowTranscript(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Call Transcript</DialogTitle>
            <DialogDescription>{showTranscript && `${showTranscript.call_type} call - ${formatDate(showTranscript.call_date)}`}</DialogDescription>
          </DialogHeader>
          {showTranscript && (
            <div className="overflow-y-auto max-h-96 p-4 bg-muted/50 rounded-lg">
              <p className="whitespace-pre-wrap text-sm text-foreground">{showTranscript.transcript || 'No transcript available for this call.'}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick View Feedback Dialog */}
      <Dialog open={!!showFeedback} onOpenChange={() => setShowFeedback(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-emerald-600" /> AI Feedback</DialogTitle>
            <DialogDescription>{showFeedback && `${showFeedback.call_type} call - ${formatDate(showFeedback.call_date)}`}</DialogDescription>
          </DialogHeader>
          {showFeedback && (
            <div className="space-y-4">
              {showFeedback.strengths?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-emerald-600 mb-2">Strengths</p>
                  <ul className="space-y-1">
                    {showFeedback.strengths.map((s, i) => (<li key={i} className="text-sm text-muted-foreground flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />{s}</li>))}
                  </ul>
                </div>
              )}
              {showFeedback.improvement_areas?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-amber-600 mb-2">Areas for Improvement</p>
                  <ul className="space-y-1">
                    {showFeedback.improvement_areas.map((a, i) => (<li key={i} className="text-sm text-muted-foreground flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />{a}</li>))}
                  </ul>
                </div>
              )}
              {showFeedback.coaching_tips?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-blue-600 mb-2">Coaching Tips</p>
                  <ul className="space-y-1">
                    {showFeedback.coaching_tips.map((t, i) => (<li key={i} className="text-sm text-muted-foreground flex items-start gap-2"><Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />{t}</li>))}
                  </ul>
                </div>
              )}
              {!showFeedback.strengths?.length && !showFeedback.improvement_areas?.length && (
                <p className="text-sm text-muted-foreground text-center py-4">No feedback available for this call.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Request Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-purple-600" /> 
              Request QA Review
            </DialogTitle>
            <DialogDescription>
              Submit your call for review by the QA team. They will listen to your call and provide detailed feedback.
            </DialogDescription>
          </DialogHeader>
          
          {reviewCall && (
            <div className="space-y-4">
              {/* Call Info */}
              <div className="p-3 bg-muted/50 dark:bg-slate-800 rounded-lg">
                <p className="font-medium text-sm">{reviewCall.filename}</p>
                <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                  <span>Score: {reviewCall.average_score?.toFixed(1) || 'N/A'}</span>
                  <span className="capitalize">{reviewCall.classification}</span>
                  <span>{formatDate(reviewCall.call_date)}</span>
                </div>
              </div>
              
              {/* Reason */}
              <div>
                <Label htmlFor="review-reason">Why do you want this call reviewed? *</Label>
                <Textarea
                  id="review-reason"
                  value={reviewReason}
                  onChange={(e) => setReviewReason(e.target.value)}
                  placeholder="Explain why you believe this call should be reviewed by QA. For example: 'I feel the score doesn't reflect the conversation quality' or 'I'd like feedback on my handling of a difficult objection'"
                  className="mt-1"
                  rows={4}
                />
              </div>
              
              {/* Expected Outcome */}
              <div>
                <Label htmlFor="review-expected">What outcome do you expect? (Optional)</Label>
                <Textarea
                  id="review-expected"
                  value={reviewExpected}
                  onChange={(e) => setReviewExpected(e.target.value)}
                  placeholder="What do you think should change? For example: 'I believe the communication score should be higher' or 'I'd like coaching tips for improvement'"
                  className="mt-1"
                  rows={2}
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={submitReviewRequest}
                  disabled={isSubmittingReview || !reviewReason.trim()}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {isSubmittingReview ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
